"use client";

import { getCasesAction } from "@/actions/search";
import { CaseData, FilterOption } from "@/cms/models";
import React, { useEffect, useState, useTransition } from "react";
import PropertyGalleryCard from "./PropertyGalleryCard";
import { usePathname, useSearchParams } from "next/navigation";
import { useRouter } from "next/navigation";
import PropertyListCard from "./PropertyListCard";
import SortCaseControl from "./ui/SortCaseControl";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faGrid,
  faList,
  faRows,
  faMap,
} from "@fortawesome/sharp-light-svg-icons";
import FilterButton from "./ui/FilterButton";
import SortingButton from "./ui/SortingButton";
import { motion, useAnimation } from "framer-motion";
import PropertyRowCard from "./PropertyRowCard";
import MapWrapper from "./ui/MapWrapper";
import TiltCard from "./ui/TiltCard";

export interface SearchFormProps {
  cases: CaseData[];
  typeFilters: FilterOption[];
  pageFilter: any;
  listViews: string[];
  sortOptions: { label: string; value: string; selected: boolean }[];
  tilt: boolean;
  shadow: "none" | "little" | "lots";
}

const PropertySearchForm: React.FC<SearchFormProps> = ({
  cases,
  typeFilters,
  pageFilter,
  sortOptions,
  listViews,
  tilt,
  shadow,
}) => {
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const initialFilter = searchParams.get("filter") || null;
  const initialQuery = searchParams.get("q") || "";

  const defaultSorting = sortOptions.find((x) => x.selected)?.value;

  const [query, setQuery] = useState<string>(initialQuery);
  const [results, setResults] = useState<CaseData[]>(cases);
  const [listView, setListViewValue] = useState<string | null>(
    listViews[0] ?? "grid"
  );
  const [animatedCount, setAnimatedCount] = useState<number>(0);
  const [currentTypeFilter, setCurrentTypeFilter] = useState<string | null>(
    initialFilter ?? "all"
  );

  const [sortKey, setSortKey] = useState<string | null>(
    defaultSorting ?? "date_desc"
  );
  const [isPending, startTransition] = useTransition();
  const [loadingFilter, setLoadingFilter] = useState<string | null>(null);

  useEffect(() => {
    const value = localStorage.getItem("list_view_mode");
    if (value && listViews.includes(value)) {
      setListViewValue(value);
    }
  }, []);

  useEffect(() => {
    const newQuery = searchParams.get("q") || "";

    if (newQuery !== query) {
      setQuery(newQuery);
      startTransition(async () => {
        const request = {
          query: newQuery,
          typeFilter: "",
          sortKey: sortKey,
        };

        const getCasesResponse = await getCasesAction({
          ...request,
          ...pageFilter,
        });

        const timestamp = new Date().getTime();
        setResults(
          getCasesResponse.items?.map((x) => ({
            ...x,
            timestamp: timestamp,
          })) ?? []
        );
      });
    }
  }, [searchParams]);

  const filterByType = async (typeFilter: string) => {
    startTransition(async () => {
      if (typeFilter === "all") {
        router.replace(`${pathname}`);
      } else {
        if (typeFilter !== currentTypeFilter) {
          router.replace(`${pathname}?filter=${typeFilter}`);
        } else {
          router.replace(`${pathname}`);
          typeFilter = "";
        }
      }

      setCurrentTypeFilter(typeFilter);
      const request = {
        query: query,
        typeFilter: typeFilter,
        sortKey: sortKey,
      };

      const getCasesResponse = await getCasesAction({
        ...request,
        ...pageFilter,
      });

      const timestamp = new Date().getTime();
      setResults(
        getCasesResponse.items?.map((x) => ({
          ...x,
          timestamp: timestamp,
        })) ?? []
      );
    });
  };

  const sortCases = async (sortKey: string) => {
    setSortKey(sortKey);

    startTransition(async () => {
      const request = {
        query: query,
        typeFilter: currentTypeFilter ?? "",
        sortKey: sortKey,
      };
      const getCasesResponse = await getCasesAction({
        ...request,
        ...pageFilter,
      });

      const timestamp = new Date().getTime();
      setResults(
        getCasesResponse.items?.map((x) => ({
          ...x,
          timestamp: timestamp,
        })) ?? []
      );
    });
  };

  const switchViews = async (view: string) => {
    setListViewValue(view);
    localStorage.setItem("list_view_mode", view);
  };

  const controls = useAnimation();

  return (
    <div className="flex flex-col p-4 gap-4">
      <div className="flex flex-col gap-2 lg:flex-row justify-between">
        <div className={`flex flex-wrap gap-2 ${query ? "invisible" : ""}`}>
          {typeFilters &&
            typeFilters.map((filter) => (
              <FilterButton
                loading={loadingFilter === filter.namespace && isPending}
                key={filter.namespace}
                label={filter.displayName}
                selected={currentTypeFilter === filter.namespace}
                onClick={async () => {
                  setLoadingFilter(filter.namespace);
                  await filterByType(filter.namespace);
                }}
              />
            ))}
        </div>
        {!pageFilter.isSold && (
          <div className="flex justify-end gap-2 items-end">
            {sortOptions?.length > 1 && (
              <SortCaseControl
                name="case-sort"
                ariaLabel="Case sort"
                items={sortOptions}
                classes="col-span-2"
                onSelected={(selectedItem) => sortCases(selectedItem.value)}
              ></SortCaseControl>
            )}
            {listViews.map((x) => {
              switch (x) {
                case "list":
                  return (
                    <SortingButton
                      key={x}
                      isSelected={listView === "list"}
                      onClick={() => switchViews("list")}
                    >
                      <FontAwesomeIcon className="size-6 flex" icon={faList} />
                    </SortingButton>
                  );

                case "grid":
                  return (
                    <SortingButton
                      key={x}
                      isSelected={listView === "grid"}
                      onClick={() => switchViews("grid")}
                    >
                      <FontAwesomeIcon className="size-6 flex" icon={faGrid} />
                    </SortingButton>
                  );

                case "row":
                  return (
                    <SortingButton
                      key={x}
                      isSelected={listView === "row"}
                      onClick={() => switchViews("row")}
                    >
                      <FontAwesomeIcon className="size-6 flex" icon={faRows} />
                    </SortingButton>
                  );

                case "map":
                  return (
                    <SortingButton
                      key={x}
                      isSelected={listView === "map"}
                      onClick={() => switchViews("map")}
                    >
                      <FontAwesomeIcon className="size-6 flex" icon={faMap} />
                    </SortingButton>
                  );
              }
            })}
          </div>
        )}
      </div>
      {query && (
        <div className="flex items-center">
          <div className="relative mr-4">
            <h1 className="block text-3xl">
              Søgeresultater for <span>&#39;{query}&#39;</span>
            </h1>
            <button
              aria-label="Clear search"
              className="absolute -top-3 -right-5 text-2xl"
              onClick={async () => {
                setQuery("");
                const request = {
                  query: "",
                  typeFilter: "",
                  sortKey: sortKey,
                };
                const getCasesResponse = await getCasesAction({
                  ...request,
                  ...pageFilter,
                });
                setResults(getCasesResponse.items ?? []);
                router.push(pathname);
              }}
              title="Slet søgning"
            >
              &times;
            </button>
          </div>
        </div>
      )}
      {results.length > 0 && listView == "grid" && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {results.map((property: any, index) => (
            <motion.section
              key={`${index}-${property.timestamp}`}
              initial="initial"
              whileInView="animate"
              animate={controls}
              viewport={{
                once: true,
                margin: "100px",
              }}
              onAnimationStart={() => setAnimatedCount(index)}
              custom={index}
              variants={{
                initial: {
                  opacity: 0,
                  y: 100,
                },
                animate: (index: number) => ({
                  opacity: 1,
                  y: 0,
                  transition: {
                    delay: 0.05 * (index - animatedCount),
                  },
                }),
              }}
            >
              {tilt ? (
                <TiltCard>
                  <PropertyGalleryCard
                    key={property.id}
                    property={property}
                    shadow={shadow}
                  />
                </TiltCard>
              ) : (
                <PropertyGalleryCard
                  key={property.id}
                  property={property}
                  shadow={shadow}
                />
              )}
            </motion.section>
          ))}
        </div>
      )}

      {results.length > 0 && listView == "row" && (
        <div className="grid grid-cols-1 gap-4">
          {results.map((property: any, index) => (
            <motion.section
              key={`${index}-${property.timestamp}`}
              initial="initial"
              whileInView="animate"
              animate={controls}
              viewport={{
                once: true,
                margin: "100px",
              }}
              onAnimationStart={() => setAnimatedCount(index)}
              custom={index}
              variants={{
                initial: {
                  opacity: 0,
                  y: 100,
                },
                animate: (index: number) => ({
                  opacity: 1,
                  y: 0,
                  transition: {
                    delay: 0.05 * (index - animatedCount),
                  },
                }),
              }}
            >
              {tilt ? (
                <TiltCard>
                  <PropertyRowCard
                    key={property.id}
                    property={property}
                    shadow={shadow}
                  />
                </TiltCard>
              ) : (
                <PropertyRowCard
                  key={property.id}
                  property={property}
                  shadow={shadow}
                />
              )}
            </motion.section>
          ))}
        </div>
      )}

      {results.length > 0 && listView == "list" && (
        <section className="flex flex-col gap-4">
          {results.map((property: any, index) => (
            <motion.section
              key={`${index}-${property.timestamp}`}
              initial="initial"
              whileInView="animate"
              animate={controls}
              viewport={{
                once: true,
                margin: "100px",
              }}
              onAnimationStart={() => setAnimatedCount(index)}
              custom={index}
              variants={{
                initial: {
                  opacity: 0,
                  y: 100,
                },
                animate: (index: number) => ({
                  opacity: 1,
                  y: 0,
                  transition: {
                    delay: 0.05 * (index - animatedCount),
                  },
                }),
              }}
            >
              {tilt ? (
                <TiltCard>
                  <PropertyListCard key={property.id} property={property} />
                </TiltCard>
              ) : (
                <PropertyListCard key={property.id} property={property} />
              )}
            </motion.section>
          ))}
        </section>
      )}

      <style jsx global>{`
        [data-search] .maplibregl-popup-content {
          padding: 0;
        }
      `}</style>

      {results.length > 0 && listView == "map" && (
        <div className="flex justify-center">
          <div className="w-full h-[80vh]" data-search>
            <MapWrapper
              markers={results
                .filter((x) => !!x.latitude && !!x.longitude)
                .map((property) => {
                  return {
                    longitude: property.longitude!,
                    latitude: property.latitude!,
                    popover: (
                      <PropertyGalleryCard
                        key={property.id}
                        property={property}
                        shadow={shadow}
                      />
                    ),
                  };
                })}
            />
          </div>
        </div>
      )}

      {results.length == 0 && (
        <div className="bg-white border p-4">
          Ingen ejendomme passede til dine søgekriterier.
        </div>
      )}
    </div>
  );
};

export default PropertySearchForm;
